/**
 * subscription-swap-shim.js
 *
 * Workaround for a bug in the RecurPay cart upsell widget ("Switch to subscription
 * and save"). This is NOT theme functionality — it compensates for third-party
 * behaviour and should be removed once RecurPay ships a fix.
 *
 * Two layers:
 *
 *   A. Resync. The theme re-renders the cart by replacing DOM subtrees
 *      (cart.js:189 innerHTML, cart.js:96 replaceWith, cart-drawer.js:77 innerHTML)
 *      and dispatches no DOM event. RecurPay only hooks window.SLIDECART_*, so its
 *      internal `items` array and the widget's positional `data-index` go stale
 *      after any cart change. We call recurpayCartV2.callCart() on cartUpdate so it
 *      rebuilds in step with the theme.
 *
 *   B. Fallback click. RecurPay builds the widget with
 *        data-default-selling-plan="<getPlanSellingPlanId(...)>"
 *      (recurpay-cart.js:1124). When that helper returns undefined, string
 *      concatenation writes the literal text "undefined" into the attribute. Their
 *      click handler's guard (`!== undefined && !== ""`) lets that string through,
 *      no plan matches it, and `plans[undefined].id` throws a TypeError inside a
 *      jQuery handler with no try/catch — so the click silently does nothing.
 *      When we detect an unusable attribute we pre-empt their handler in the
 *      capture phase and perform the swap ourselves, using the native selling plan
 *      id rendered onto the cart row by Liquid (data-rp-selling-plan).
 *
 * Every access to RecurPay internals is guarded. If they rename things, layer B
 * stops firing and layer A no-ops — the button reverts to its current behaviour
 * rather than breaking in a new way.
 */
(function () {
  'use strict';

  // The drawer renders on every template, so on /cart this file is requested by
  // both cart-drawer.liquid and main-cart-items.liquid. Bind once.
  if (window.__subscriptionSwapShimLoaded) return;
  window.__subscriptionSwapShimLoaded = true;

  var BUTTON_SELECTOR = '.subscribe-text-button';
  var WRAPPER_SELECTOR = '.upgrade-icon-wrapper';
  var LOADING_CLASS = 'btn-loading';
  var LOG_PREFIX = 'subscription-swap-shim:';

  function recurpay() {
    return typeof window !== 'undefined' ? window.recurpayCartV2 : undefined;
  }

  /** RecurPay writes the string "undefined"/"null" when its lookup fails. */
  function isUsablePlanValue(value) {
    return !!value && value !== 'undefined' && value !== 'null' && value !== 'NaN';
  }

  // ---------------------------------------------------------------- Layer A

  function resyncRecurpay() {
    var rp = recurpay();
    if (!rp || typeof rp.callCart !== 'function') return;

    try {
      rp.callCart();
    } catch (error) {
      console.warn(LOG_PREFIX, 'recurpayCartV2.callCart() failed during resync', error);
    }
  }

  function subscribeToCartUpdates() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

    subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      // Our own swap already resyncs; don't double-fire.
      if (event && event.source === 'subscription-swap-shim') return;
      resyncRecurpay();
    });
  }

  // ---------------------------------------------------------------- Layer B

  function setLoading(wrapper, isLoading) {
    if (!wrapper) return;
    wrapper.classList[isLoading ? 'add' : 'remove'](LOADING_CLASS);
  }

  /**
   * Re-renders the cart the same way Dawn does on a pub/sub cart update
   * (mirrors assets/cart.js:88-118).
   */
  function rerenderCart(inDrawer) {
    var sectionId = inDrawer ? 'cart-drawer' : 'main-cart-items';

    return fetch(window.routes.cart_url + '?section_id=' + sectionId)
      .then(function (response) {
        if (!response.ok) throw new Error('section fetch failed: HTTP ' + response.status);
        return response.text();
      })
      .then(function (responseText) {
        var html = new DOMParser().parseFromString(responseText, 'text/html');
        var selectors = inDrawer ? ['cart-drawer-items', '.cart-drawer__footer'] : ['cart-items'];

        selectors.forEach(function (selector) {
          var target = document.querySelector(selector);
          var source = html.querySelector(selector);
          if (target && source) target.replaceWith(source);
        });
      });
  }

  function handleSwap(button) {
    var wrapper = button.closest(WRAPPER_SELECTOR);
    var row = button.closest('[data-rp-line]');

    if (!row) {
      console.warn(LOG_PREFIX, 'no cart row with [data-rp-line] found for this button; leaving it alone');
      return;
    }

    var line = row.getAttribute('data-rp-line');
    var sellingPlan = row.getAttribute('data-rp-selling-plan');
    var quantity = parseInt(row.getAttribute('data-rp-quantity'), 10);

    if (!isUsablePlanValue(sellingPlan)) {
      console.warn(
        LOG_PREFIX,
        'this line has no native selling plan available (data-rp-selling-plan is absent);',
        'cannot convert it. Line:',
        line
      );
      return;
    }

    var inDrawer = !!button.closest('cart-drawer');
    setLoading(wrapper, true);

    fetch(window.routes.cart_change_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        line: parseInt(line, 10),
        quantity: isNaN(quantity) ? 1 : quantity,
        selling_plan: parseInt(sellingPlan, 10),
      }),
    })
      .then(function (response) {
        if (!response.ok) throw new Error('cart/change failed: HTTP ' + response.status);
        return response.json();
      })
      .then(function (cartData) {
        return rerenderCart(inDrawer).then(function () {
          return cartData;
        });
      })
      .then(function (cartData) {
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'subscription-swap-shim',
            cartData: cartData,
          });
        }
        resyncRecurpay();
      })
      .catch(function (error) {
        console.error(LOG_PREFIX, 'subscription swap failed', error);
      })
      .finally(function () {
        setLoading(wrapper, false);
      });
  }

  /**
   * Capture phase, so this runs before RecurPay's bubble-phase delegated handler
   * and can stop the click reaching the code path that throws.
   */
  function interceptClick(event) {
    var target = event.target;
    if (!target || typeof target.closest !== 'function') return;

    var button = target.closest(BUTTON_SELECTOR);
    if (!button) return;

    var wrapper = button.closest(WRAPPER_SELECTOR);
    var planValue = wrapper ? wrapper.getAttribute('data-default-selling-plan') : null;

    // RecurPay's own data is fine — let it do its job.
    if (isUsablePlanValue(planValue)) return;

    console.warn(
      LOG_PREFIX,
      'RecurPay widget has an unusable data-default-selling-plan (' +
        JSON.stringify(planValue) +
        '); its click handler would throw. Taking over the subscription swap.'
    );

    event.preventDefault();
    event.stopPropagation();
    handleSwap(button);
  }

  document.addEventListener('click', interceptClick, true);
  subscribeToCartUpdates();
})();
